import find from 'lodash/find';
import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
import config from '../config';
import { createSlug, parse, stringify } from './urlHelpers';

const findRouteByName = (nameToFind, routes) => find(routes, route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
// The all-stores, all-genders store page is the storefront at the homepage address (BSC-43), so
// every link and redirect to it resolves to /.
const isStorefrontHome = (name, params) =>
  name === 'LandingPageListingStore' && params?.type === 'all' && params?.gender === 'all';

export const pathByRouteName = (nameToFind, routes, params = {}) => {
  if (isStorefrontHome(nameToFind, params)) {
    return '/';
  }
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

// Route names whose canonical form is the clean listing URL /l/{slug}/{id}
const LISTING_ROUTE_NAMES = ['ListingPage', 'ListingPageCanonical'];
// Paginated store landing pages keep ?page= so that each page canonicalises to itself
const PAGINATED_ROUTE_NAMES = ['LandingPage', 'LandingPageListingStore'];

const flatten = (acc, val) => acc.concat(val);

// Only params that change the result set are kept in a search page canonical: the configured
// filters (pub_*, price, keywords) and any other pub_* param that internal links use
// (e.g. pub_listingStore). Sort, page, map and location params are dropped.
const isSearchFilterParam = key => {
  const filterParamNames = (config.custom.filters || [])
    .map(f => f.queryParamNames)
    .reduce(flatten, []);
  return key.startsWith('pub_') || filterParamNames.includes(key);
};

const pickParams = (search, isKept) => {
  const params = parse(search || '');
  return Object.keys(params).reduce(
    (picked, key) =>
      isKept(key) && params[key] !== '' && params[key] != null
        ? { ...picked, [key]: params[key] }
        : picked,
    {}
  );
};

const withSearch = (pathname, params) => {
  const searchString = stringify(params);
  return searchString.length > 0 ? `${pathname}?${searchString}` : pathname;
};

/**
 * Get the canonical URL path from the given location
 *
 * - ListingPage and ListingPageCanonical: /l/{slug}/{id}, where the slug is created from the
 *   listing title when it is given in canonicalParams. No query string.
 * - SearchPage: /s plus filter params only (no sort, page, map or location params).
 * - LandingPage (the storefront at /) and LandingPageListingStore: the path plus ?page= only.
 * - Every other route: the path only.
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 * @param {Boolean} pathOnly - return the pathname only (used with a strict referrer policy)
 * @param {Object} canonicalParams - optional { listingTitle }
 *
 * @return {String} Canonical URL path of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false, canonicalParams = {}) => {
  const { pathname = '', search = '' } = location;
  const cleanedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  const matches = matchPathname(cleanedPathname, routes);
  const match = matches.length > 0 ? matches[0] : null;
  const routeName = match ? match.route.name : null;

  if (LISTING_ROUTE_NAMES.includes(routeName)) {
    const { id, slug: slugInPath } = match.params;
    const { listingTitle } = canonicalParams || {};
    const slug = listingTitle ? createSlug(listingTitle) : slugInPath;
    return slug ? `/l/${slug}/${id}` : `/l/${id}`;
  }

  if (pathOnly) {
    return cleanedPathname;
  }

  if (routeName === 'SearchPage') {
    return withSearch(cleanedPathname, pickParams(search, isSearchFilterParam));
  }

  if (PAGINATED_ROUTE_NAMES.includes(routeName)) {
    const { page } = pickParams(search, key => key === 'page');
    const pageNumber = Number.parseInt(page, 10);
    return pageNumber > 1 ? withSearch(cleanedPathname, { page: pageNumber }) : cleanedPathname;
  }

  return cleanedPathname;
};
